import { compact } from "lodash";
import { TFunction } from "@/i18n/tt";
import { Company } from "@/types/CompanyInfo";
import { ShareholderDetailsResponseDTO } from "@/types/Shareholder";
import type { OpenProject } from "@/dbot/project/projectDomain";
import { ArchiveState, getOpenEntryLabel } from "@/archive/archiveDomain";
import { when } from "@/utils/functions";
import { isAdminRoute, isBoardMemberRoute, isShareholderRoute, MatchedDCRoute } from "./routes";

const appName = "dCompany";

export type PageTitleItems = {
  shareholder?: Pick<ShareholderDetailsResponseDTO, "shareholderId" | "shareholderName">;
  project?: OpenProject;
  archive?: ArchiveState;
};

const withAppName = (parts: (string | undefined)[]): string =>
  compact([...parts, appName]).join(" - ");

const companyOrgNo = (route: MatchedDCRoute | undefined): string | undefined =>
  isAdminRoute(route) || isBoardMemberRoute(route) || isShareholderRoute(route)
    ? route.params.orgNo
    : undefined;

const itemName = (
  route: MatchedDCRoute | undefined,
  { shareholder, project, archive }: PageTitleItems,
  t: TFunction,
): string | undefined => {
  if (!isAdminRoute(route)) {
    return undefined;
  }
  switch (route.name) {
    case "admin/shareholder-details":
      return shareholder?.shareholderId === Number(route.params.id)
        ? shareholder.shareholderName
        : undefined;
    case "admin/project":
      return project?.projectId === route.params.projectId ? project.name : undefined;
    case "admin/archive":
      return archive?.rootFolder !== undefined && archive.rootFolder === route.params.rootFolder
        ? when(getOpenEntryLabel(archive), (label) => t(label))
        : undefined;
    default:
      return undefined;
  }
};

export const pageTitle = ({
  route,
  companies,
  items,
  t,
}: {
  route: MatchedDCRoute | undefined;
  companies: (Pick<Company, "name" | "organizationnumber"> | undefined)[];
  items: PageTitleItems;
  t: TFunction;
}): string =>
  withAppName([
    itemName(route, items, t) || (route?.data?.title && t(route.data.title)),
    companies.find((c) => c !== undefined && c.organizationnumber === companyOrgNo(route))?.name,
  ]);

export const loginPageTitle = (t: TFunction): string => withAppName([t("landing.login")]);
